package dev.kuml.portal.web.i18n.strings

/**
 * Everything under `dev.kuml.portal.web.editor` -- `EditorScreen.kt` (the largest single
 * consumer), `DiagramTabs.kt`, `ZoomToolbar.kt`, `DslPanelState.kt`, `GenerateState.kt`,
 * `ReportAvailability.kt`, `CollabPresenceFormat.kt`, and `DiagramFileName.kt`'s translated
 * fallback base name -- kept in ONE interface rather than one per file: every one of those files
 * is a small piece of the SAME screen, and a caller reaching for editor copy should find all of it
 * under `t.editor.*` without needing to know the internal file split. Fields are grouped by origin
 * file via the section comments below, matching this wave's plan §5.5/§5.6.
 */
public interface EditorStrings {
    // ---- EditorScreen.kt: example / empty state ----
    public val examplePrompt: String
    public val exampleChipLabel: String
    public val emptyStageIntro: String

    // ---- EditorScreen.kt: subheader ----
    public val backButton: String
    public val backAriaLabel: String

    // ---- EditorScreen.kt: prompt area ----
    public val promptLabel: String
    public val promptPlaceholder: String

    // ---- EditorScreen.kt: attach-a-context-file (R-8) ----
    public val attachButton: String
    public val attachHint: String
    public val attachChipRemoveAriaLabel: String
    public val checkingFile: String
    public val unsupportedFormat: String
    public val unsupportedFormatDetail: String
    public val fileTooLarge: String
    public val fileTooLargeDetail: String
    public val contextExpired: String
    public val contextExpiredDetail: String
    public val contextUploadFailed: String

    public fun attachedFile(label: String): String

    // ---- EditorScreen.kt: generation ----
    public val generate: String
    public val generating: String
    public val cancelled: String
    public val diagramCreated: String
    public val diagramUpdated: String
    public val diagramAlt: String

    public fun generateWithCountdown(seconds: Long): String

    // ---- EditorScreen.kt: PNG export (R-7) ----
    public val pngExportFailed: String
    public val pngExportFailedDetail: String

    // ---- EditorScreen.kt: report/comment thread (R-9/R-25) ----
    public val reportButton: String
    public val reportedButton: String
    public val reportFormLabel: String
    public val reportSubmit: String
    public val reportThanks: String
    public val alreadyReported: String
    public val alreadyReportedDetail: String
    public val reportFailed: String
    public val replyPlaceholder: String
    public val sendReply: String
    public val replySent: String

    /** Not translated -- product name (see `EditorScreen.kt`'s `renderThread` KDoc). */
    public val adminAuthorLabel: String get() = "kUML-Team"
    public val verifiedBadge: String
    public val formerMember: String
    public val noGenerationHint: String

    // ---- DslPanelState.kt (R-24) ----
    public val dslToggleCollapsed: String
    public val dslToggleExpanded: String
    public val dslLoading: String
    public val dslEmpty: String
    public val dslError: String
    public val storedDiagramUnrenderable: String

    // ---- DiagramFileName.kt ----
    public val diagramFileFallback: String

    /** DISPLAY-ONLY localisation of the server's `"Diagramm N"` default diagram name -- see
     * `projects/DefaultNameDisplay.kt`'s KDoc; never used for comparison or persistence. */
    public fun defaultDiagramName(n: Int): String

    // ---- DiagramTabs.kt ----
    public val tabRenameAriaLabel: String
    public val tabDeleteAriaLabel: String
    public val tabNewAriaLabel: String
    public val tabCreateFailed: String
    public val tabListLoadFailed: String
    public val tabLiveRoom: String
    public val tabDeleteFailed: String
    public val tabMinOneDiagram: String
    public val tabMinOneDiagramDetail: String

    public fun tabDiagramLimitReached(max: Int): String

    public fun tabDeleteConfirmQuestion(name: String): String

    // ---- ZoomToolbar.kt (R-7) ----
    public val zoomToolbarAriaLabel: String
    public val zoomInLabel: String
    public val zoomOutLabel: String
    public val zoomFitLabel: String
    public val downloadSvgLabel: String
    public val downloadPngLabel: String

    // ---- CollabPresenceFormat.kt ----
    public fun collabIndicator(otherConnections: Int): String?

    // ---- CollabPresenceFormat.kt (R-28: per-peer avatar tooltip) ----
    public fun collabPeerPresent(displayName: String): String

    public fun collabPeerSelected(
        displayName: String,
        elementName: String,
    ): String

    // ---- editor/InspectorPanel.kt (R-27, interactive diagram-editing UI) ----
    public val inspectorNoSelectionHint: String
    public val inspectorAddClassButton: String
    public val inspectorNameLabel: String
    public val inspectorVisibilityLabel: String
    public val inspectorAbstractLabel: String
    public val inspectorAttributesHeading: String
    public val inspectorOperationsHeading: String
    public val inspectorRelationshipsHeading: String
    public val inspectorAddAttributeButton: String
    public val inspectorAddOperationButton: String
    public val inspectorCreateAssociationButton: String
    public val inspectorDeleteButton: String
    public val inspectorTwoSelectedHint: String

    // ---- editor/InspectorPanel.kt: delete confirmation (cascading relationships) ----
    public val confirmDeleteTitle: String
    public val confirmDeleteConfirmButton: String
    public val confirmDeleteCancelButton: String

    public fun confirmDeleteQuestion(elementName: String): String

    public fun confirmDeleteCascadeWarning(cascadeCount: Int): String

    // ---- editor/ModelOpFeedback.kt: a rejected ModelOp's status-line copy ----
    public val opRejectedMalformed: String
    public val opRejectedUnknownElement: String
    public val opRejectedLimitExceeded: String
    public val opRejectedInvalidValue: String
    public val opRejectedNoLiveRoom: String

    public val allPlainStrings: List<String>
        get() =
            listOf(
                examplePrompt,
                exampleChipLabel,
                emptyStageIntro,
                backButton,
                backAriaLabel,
                promptLabel,
                promptPlaceholder,
                attachButton,
                attachHint,
                attachChipRemoveAriaLabel,
                checkingFile,
                unsupportedFormat,
                unsupportedFormatDetail,
                fileTooLarge,
                fileTooLargeDetail,
                contextExpired,
                contextExpiredDetail,
                contextUploadFailed,
                generate,
                generating,
                cancelled,
                diagramCreated,
                diagramUpdated,
                diagramAlt,
                pngExportFailed,
                pngExportFailedDetail,
                reportButton,
                reportedButton,
                reportFormLabel,
                reportSubmit,
                reportThanks,
                alreadyReported,
                alreadyReportedDetail,
                reportFailed,
                replyPlaceholder,
                sendReply,
                replySent,
                adminAuthorLabel,
                verifiedBadge,
                formerMember,
                noGenerationHint,
                dslToggleCollapsed,
                dslToggleExpanded,
                dslLoading,
                dslEmpty,
                dslError,
                storedDiagramUnrenderable,
                diagramFileFallback,
                tabRenameAriaLabel,
                tabDeleteAriaLabel,
                tabNewAriaLabel,
                tabCreateFailed,
                tabListLoadFailed,
                tabLiveRoom,
                tabDeleteFailed,
                tabMinOneDiagram,
                tabMinOneDiagramDetail,
                zoomToolbarAriaLabel,
                zoomInLabel,
                zoomOutLabel,
                zoomFitLabel,
                downloadSvgLabel,
                downloadPngLabel,
                inspectorNoSelectionHint,
                inspectorAddClassButton,
                inspectorNameLabel,
                inspectorVisibilityLabel,
                inspectorAbstractLabel,
                inspectorAttributesHeading,
                inspectorOperationsHeading,
                inspectorRelationshipsHeading,
                inspectorAddAttributeButton,
                inspectorAddOperationButton,
                inspectorCreateAssociationButton,
                inspectorDeleteButton,
                inspectorTwoSelectedHint,
                confirmDeleteTitle,
                confirmDeleteConfirmButton,
                confirmDeleteCancelButton,
                opRejectedMalformed,
                opRejectedUnknownElement,
                opRejectedLimitExceeded,
                opRejectedInvalidValue,
                opRejectedNoLiveRoom,
            )
}

public object EditorStringsDe : EditorStrings {
    override val examplePrompt = "Ein Nutzer meldet sich an, der Server prüft die Anmeldung gegen die Datenbank."
    override val exampleChipLabel = "✦ Anmeldung: Nutzer → Server → Datenbank"
    override val emptyStageIntro =
        "Noch kein Diagramm. Beschreiben Sie im Eingabefeld, was Sie modellieren möchten — " +
            "oder starten Sie mit einem Beispiel:"
    override val backButton = "← Zurück"
    override val backAriaLabel = "Zurück zur Projektliste"
    override val promptLabel = "Was möchten Sie modellieren?"
    override val promptPlaceholder =
        "Beschreiben Sie Ihr System, etwa: Ein Nutzer meldet sich an, der Server prüft die " +
            "Anmeldung gegen die Datenbank."
    override val attachButton = "📎 Datei anhängen"
    override val attachHint = "CSV, JSON, Markdown oder Text · max. 64 KB"
    override val attachChipRemoveAriaLabel = "Angehängte Datei entfernen"
    override val checkingFile = "Datei wird geprüft …"
    override val unsupportedFormat = "Nicht unterstütztes Dateiformat."
    override val unsupportedFormatDetail = "Erlaubt sind CSV, JSON, Markdown und Textdateien."
    override val fileTooLarge = "Die Datei ist zu groß."
    override val fileTooLargeDetail = "Maximal 64 KB je Kontextdatei."
    override val contextExpired = "Die angehängte Datei ist abgelaufen."
    override val contextExpiredDetail = "Bitte hängen Sie sie erneut an."
    override val contextUploadFailed = "Die Datei konnte nicht als Kontext verwendet werden."
    override val generate = "Diagramm generieren"
    override val generating = "Diagramm wird erzeugt…"
    override val cancelled = "Generierung abgebrochen. Ihr Diagramm ist unverändert."
    override val diagramCreated = "Diagramm erstellt"
    override val diagramUpdated = "Diagramm aktualisiert"
    override val diagramAlt = "Generiertes Diagramm"
    override val pngExportFailed = "Der PNG-Export ist fehlgeschlagen."
    override val pngExportFailedDetail = "Bitte laden Sie das Diagramm stattdessen als SVG herunter."
    override val reportButton = "⚑ Diagramm als fehlerhaft melden"
    override val reportedButton = "✓ Als fehlerhaft gemeldet"
    override val reportFormLabel = "Was ist an diesem Diagramm falsch?"
    override val reportSubmit = "Melden"
    override val reportThanks = "Vielen Dank — Ihre Meldung ist eingegangen."
    override val alreadyReported = "Diese Generierung wurde bereits gemeldet."
    override val alreadyReportedDetail = "Sie können den Verlauf unten ergänzen."
    override val reportFailed = "Die Meldung konnte nicht gesendet werden."
    override val replyPlaceholder = "Antwort schreiben …"
    override val sendReply = "Antwort senden"
    override val replySent = "Antwort gesendet."
    override val verifiedBadge = "Verifiziert"
    override val formerMember = "Ehemaliges Mitglied"
    override val noGenerationHint = "Ein Fehlerbericht ist erst nach einer KI-Generierung für dieses Diagramm möglich."
    override val dslToggleCollapsed = "▸ DSL anzeigen"
    override val dslToggleExpanded = "▾ DSL anzeigen"
    override val dslLoading = "DSL wird geladen …"
    override val dslEmpty = "Für dieses Diagramm ist noch keine DSL gespeichert. Erzeugen Sie zuerst ein Diagramm."
    override val dslError = "Die DSL konnte nicht geladen werden."
    override val storedDiagramUnrenderable = "Das gespeicherte Diagramm konnte nicht angezeigt werden."
    override val diagramFileFallback = "kuml-diagramm"
    override val tabRenameAriaLabel = "Diagramm umbenennen"
    override val tabDeleteAriaLabel = "Diagramm löschen"
    override val tabNewAriaLabel = "Neues Diagramm"
    override val tabCreateFailed = "Das Diagramm konnte nicht angelegt werden."
    override val tabListLoadFailed = "Die Diagrammliste konnte nicht geladen werden."
    override val tabLiveRoom = "In diesem Diagramm wird gerade gemeinsam gearbeitet."
    override val tabDeleteFailed = "Löschen fehlgeschlagen. Bitte erneut versuchen."
    override val tabMinOneDiagram = "Ein Projekt braucht mindestens ein Diagramm."
    override val tabMinOneDiagramDetail = "Löschen Sie stattdessen das gesamte Projekt."
    override val zoomToolbarAriaLabel = "Diagramm-Werkzeuge"
    override val zoomInLabel = "Vergrößern"
    override val zoomOutLabel = "Verkleinern"
    override val zoomFitLabel = "Ansicht einpassen"
    override val downloadSvgLabel = "Als SVG herunterladen"
    override val downloadPngLabel = "Als PNG herunterladen"

    override fun attachedFile(label: String): String = "Datei angehängt: $label"

    override fun generateWithCountdown(seconds: Long): String = "Diagramm generieren ($seconds s)"

    override fun defaultDiagramName(n: Int): String = "Diagramm $n"

    override fun tabDiagramLimitReached(max: Int): String =
        "Free-Plan: maximal $max Diagramme je Projekt. Löschen Sie ein Diagramm, um Platz zu schaffen."

    override fun tabDeleteConfirmQuestion(name: String): String = "Diagramm \"$name\" löschen? Das kann nicht rückgängig gemacht werden."

    override fun collabIndicator(otherConnections: Int): String? =
        when {
            otherConnections <= 0 -> null
            otherConnections == 1 -> "1 andere Verbindung aktiv"
            else -> "$otherConnections andere Verbindungen aktiv"
        }

    override fun collabPeerPresent(displayName: String): String = displayName

    override fun collabPeerSelected(
        displayName: String,
        elementName: String,
    ): String = "$displayName — $elementName ausgewählt"

    override val inspectorNoSelectionHint = "Wählen Sie ein Element im Diagramm aus, um es zu bearbeiten."
    override val inspectorAddClassButton = "+ Klasse"
    override val inspectorNameLabel = "Name"
    override val inspectorVisibilityLabel = "Sichtbarkeit"
    override val inspectorAbstractLabel = "Abstrakt"
    override val inspectorAttributesHeading = "Attribute"
    override val inspectorOperationsHeading = "Operationen"
    override val inspectorRelationshipsHeading = "Beziehungen"
    override val inspectorAddAttributeButton = "+ Attribut"
    override val inspectorAddOperationButton = "+ Methode"
    override val inspectorCreateAssociationButton = "Assoziation erstellen"
    override val inspectorDeleteButton = "Löschen"
    override val inspectorTwoSelectedHint = "Zwei Elemente ausgewählt."
    override val confirmDeleteTitle = "Element löschen?"
    override val confirmDeleteConfirmButton = "Löschen"
    override val confirmDeleteCancelButton = "Abbrechen"
    override val opRejectedMalformed = "Die Änderung konnte nicht verarbeitet werden."
    override val opRejectedUnknownElement = "Dieses Element existiert nicht mehr — bitte aktualisieren."
    override val opRejectedLimitExceeded = "Das Diagramm hat sein Limit erreicht."
    override val opRejectedInvalidValue = "Dieser Wert ist ungültig."
    override val opRejectedNoLiveRoom = "Die Verbindung wurde unterbrochen — bitte neu laden."

    override fun confirmDeleteQuestion(elementName: String): String =
        "\"$elementName\" wirklich löschen? Das kann nicht rückgängig gemacht werden."

    override fun confirmDeleteCascadeWarning(cascadeCount: Int): String =
        when (cascadeCount) {
            0 -> ""
            1 -> "Dabei wird zusätzlich 1 Beziehung entfernt."
            else -> "Dabei werden zusätzlich $cascadeCount Beziehungen entfernt."
        }
}

public object EditorStringsEn : EditorStrings {
    override val examplePrompt = "A user logs in, and the server checks the login against the database."
    override val exampleChipLabel = "✦ Login: User → Server → Database"
    override val emptyStageIntro =
        "No diagram yet. Describe what you'd like to model in the field below — or start from " +
            "an example:"
    override val backButton = "← Back"
    override val backAriaLabel = "Back to project list"
    override val promptLabel = "What would you like to model?"
    override val promptPlaceholder =
        "Describe your system, e.g.: A user logs in, and the server checks the login against " +
            "the database."
    override val attachButton = "📎 Attach file"
    override val attachHint = "CSV, JSON, Markdown, or text · max. 64 KB"
    override val attachChipRemoveAriaLabel = "Remove attached file"
    override val checkingFile = "Checking file …"
    override val unsupportedFormat = "Unsupported file format."
    override val unsupportedFormatDetail = "Allowed: CSV, JSON, Markdown, and plain text files."
    override val fileTooLarge = "The file is too large."
    override val fileTooLargeDetail = "Maximum 64 KB per context file."
    override val contextExpired = "The attached file has expired."
    override val contextExpiredDetail = "Please attach it again."
    override val contextUploadFailed = "The file could not be used as context."
    override val generate = "Generate diagram"
    override val generating = "Generating diagram…"
    override val cancelled = "Generation cancelled. Your diagram is unchanged."
    override val diagramCreated = "Diagram created"
    override val diagramUpdated = "Diagram updated"
    override val diagramAlt = "Generated diagram"
    override val pngExportFailed = "The PNG export failed."
    override val pngExportFailedDetail = "Please download the diagram as SVG instead."
    override val reportButton = "⚑ Report diagram as incorrect"
    override val reportedButton = "✓ Reported as incorrect"
    override val reportFormLabel = "What's wrong with this diagram?"
    override val reportSubmit = "Report"
    override val reportThanks = "Thank you — your report has been received."
    override val alreadyReported = "This generation has already been reported."
    override val alreadyReportedDetail = "You can add to the thread below."
    override val reportFailed = "The report could not be sent."
    override val replyPlaceholder = "Write a reply …"
    override val sendReply = "Send reply"
    override val replySent = "Reply sent."
    override val verifiedBadge = "Verified"
    override val formerMember = "Former member"
    override val noGenerationHint = "A report is only possible after an AI generation for this diagram."
    override val dslToggleCollapsed = "▸ Show DSL"
    override val dslToggleExpanded = "▾ Show DSL"
    override val dslLoading = "Loading DSL …"
    override val dslEmpty = "No DSL is stored for this diagram yet. Generate a diagram first."
    override val dslError = "The DSL could not be loaded."
    override val storedDiagramUnrenderable = "The stored diagram could not be displayed."
    override val diagramFileFallback = "kuml-diagram"
    override val tabRenameAriaLabel = "Rename diagram"
    override val tabDeleteAriaLabel = "Delete diagram"
    override val tabNewAriaLabel = "New diagram"
    override val tabCreateFailed = "The diagram could not be created."
    override val tabListLoadFailed = "The diagram list could not be loaded."
    override val tabLiveRoom = "Someone is currently collaborating on this diagram."
    override val tabDeleteFailed = "Delete failed. Please try again."
    override val tabMinOneDiagram = "A project needs at least one diagram."
    override val tabMinOneDiagramDetail = "Delete the entire project instead."
    override val zoomToolbarAriaLabel = "Diagram tools"
    override val zoomInLabel = "Zoom in"
    override val zoomOutLabel = "Zoom out"
    override val zoomFitLabel = "Fit to view"
    override val downloadSvgLabel = "Download as SVG"
    override val downloadPngLabel = "Download as PNG"

    override fun attachedFile(label: String): String = "File attached: $label"

    override fun generateWithCountdown(seconds: Long): String = "Generate diagram ($seconds s)"

    override fun defaultDiagramName(n: Int): String = "Diagram $n"

    override fun tabDiagramLimitReached(max: Int): String = "Free plan: up to $max diagrams per project. Delete a diagram to make room."

    override fun tabDeleteConfirmQuestion(name: String): String = "Delete diagram \"$name\"? This cannot be undone."

    override fun collabIndicator(otherConnections: Int): String? =
        when {
            otherConnections <= 0 -> null
            otherConnections == 1 -> "1 other connection active"
            else -> "$otherConnections other connections active"
        }

    override fun collabPeerPresent(displayName: String): String = displayName

    override fun collabPeerSelected(
        displayName: String,
        elementName: String,
    ): String = "$displayName — selected $elementName"

    override val inspectorNoSelectionHint = "Select an element in the diagram to edit it."
    override val inspectorAddClassButton = "+ Class"
    override val inspectorNameLabel = "Name"
    override val inspectorVisibilityLabel = "Visibility"
    override val inspectorAbstractLabel = "Abstract"
    override val inspectorAttributesHeading = "Attributes"
    override val inspectorOperationsHeading = "Operations"
    override val inspectorRelationshipsHeading = "Relationships"
    override val inspectorAddAttributeButton = "+ Attribute"
    override val inspectorAddOperationButton = "+ Operation"
    override val inspectorCreateAssociationButton = "Create association"
    override val inspectorDeleteButton = "Delete"
    override val inspectorTwoSelectedHint = "Two elements selected."
    override val confirmDeleteTitle = "Delete element?"
    override val confirmDeleteConfirmButton = "Delete"
    override val confirmDeleteCancelButton = "Cancel"
    override val opRejectedMalformed = "That change could not be processed."
    override val opRejectedUnknownElement = "This element no longer exists — please refresh."
    override val opRejectedLimitExceeded = "The diagram has reached its limit."
    override val opRejectedInvalidValue = "That value is invalid."
    override val opRejectedNoLiveRoom = "The connection was lost — please reload."

    override fun confirmDeleteQuestion(elementName: String): String = "Delete \"$elementName\"? This cannot be undone."

    override fun confirmDeleteCascadeWarning(cascadeCount: Int): String =
        when (cascadeCount) {
            0 -> ""
            1 -> "This will also remove 1 relationship."
            else -> "This will also remove $cascadeCount relationships."
        }
}
